// Add / Edit a Component History row — the slide-in over the Component History tab (2026-10-14).
// Chrome is the shared Sheet (BP-89); the save is POST / PATCH /v6/items/{id}/component-history,
// the rules are Aeronet.app app/items/component_history.py.
//
// Legacy (intent): the inline row editor of mod_parts/includes/tabs/parts_tab_component_history.inc
// :501-644 -> save_history (parts_ajax_handler.inc:1757-1794): Type (Install / Removal / N/A /
// Ops Installed / Ops Removed), Based On, Date, Aircraft REG/ENG No., A/C@Unit, TSN or UNK, TSO,
// Comment. A new row is type 'Manual'; an edit keeps the row's own type.
import React, { useMemo, useRef } from "react";
import { Autocomplete, Checkbox, FormControlLabel, Grid, MenuItem, TextField as MuiTextField } from "@mui/material";
import { Field, Form, Formik, FormikHelpers, FormikProps } from "formik";
import { TextField } from "formik-mui";
import { useSnackbar } from "notistack";

import { axios } from "../../../api";
import { ActionButton, CancelButton } from "../../../components/Buttons";
import Sheet, { SheetActions, SheetContent, SheetHeader } from "../../../components/Sheet";
import TenantDatePicker from "../../../components/TenantDatePicker";
import { handleAPIErrors } from "../../../utils/FormikErrors";
import { tsoOverTsn } from "../componentLimitations";
import type { ComponentHistoryRow } from "./ComponentHistory";

/** Legacy's Type select (parts_tab_component_history.inc:521-527); the API refuses any other. */
export const HISTORY_TYPES = ["Install", "Removal", "N/A", "Ops Installed", "Ops Removed"] as const;

export interface HistoryFormValues {
  history_type: string;
  based_on: string;
  /** "YYYY-MM-DD" or "" (TenantDatePicker's wire form). */
  record_date: string;
  ac_info: string;
  ac_unit: string;
  tsn: string;
  tsn_unknown: boolean;
  tso: string;
  comment: string;
}

const str = (v: unknown) => (v === null || v === undefined ? "" : String(v));
const numOrNull = (v: string) => (v.trim() === "" ? null : v.trim());

export function historyFormValues(row: ComponentHistoryRow | null): HistoryFormValues {
  if (!row) {
    return {
      history_type: "Install",
      based_on: "",
      record_date: "",
      ac_info: "",
      ac_unit: "",
      tsn: "",
      tsn_unknown: false,
      tso: "",
      comment: "",
    };
  }
  const unknown = row.tsn === "UNK";
  return {
    history_type: str(row.history_type),
    based_on: str(row.based_on),
    record_date: str(row.record_date).slice(0, 10),
    ac_info: str(row.ac_info),
    ac_unit: str(row.ac_unit),
    tsn: unknown ? "" : str(row.tsn),
    tsn_unknown: unknown,
    tso: str(row.tso),
    // the grid shows the row's type where it has no comment (parts_ajax_handler.inc:2487): not a comment
    comment: row.comment && row.comment !== row.type ? row.comment : "",
  };
}

/** The POST / PATCH body. An edit sends the Type only when it changes it: a row v6 or legacy wrote
 * itself may carry another or no Type ("To Flying"), which the API keeps (component_history.py). */
export function historyPayload(values: HistoryFormValues, initialType: string | null = null): Record<string, unknown> {
  const body: Record<string, unknown> = {
    ...(initialType === null || values.history_type !== initialType ? { history_type: values.history_type } : {}),
    based_on: values.based_on.trim() || null,
    record_date: values.record_date || null,
    ac_info: values.ac_info.trim() || null,
    ac_unit: numOrNull(values.ac_unit),
    tso: numOrNull(values.tso),
    comment: values.comment.trim() || null,
  };
  if (values.tsn_unknown) body.tsn_unknown = true;
  else body.tsn = numOrNull(values.tsn);
  return body;
}

export function historyFormErrors(
  values: HistoryFormValues,
  initialType: string | null = null
): Partial<Record<keyof HistoryFormValues, string>> {
  const errors: Partial<Record<keyof HistoryFormValues, string>> = {};
  const typeChanged = initialType === null || values.history_type !== initialType;
  if (typeChanged && !HISTORY_TYPES.includes(values.history_type as (typeof HISTORY_TYPES)[number])) {
    errors.history_type = "Type is required";
  }
  if (!values.record_date) errors.record_date = "Date is required";
  const tsn = values.tsn_unknown || numOrNull(values.tsn) === null ? null : Number(values.tsn);
  const tso = numOrNull(values.tso) === null ? null : Number(values.tso);
  const over = tsoOverTsn(tsn, tso);
  if (over) errors.tso = over.replace(/ at install/g, "");
  return errors;
}

interface Props {
  itemId: number;
  /** The row being edited; null adds one. */
  row: ComponentHistoryRow | null;
  /** Based On values already in the history (the stored spellings). */
  basedOnOptions: string[];
  onClose: () => void;
  onSaved: () => void;
}

const ComponentHistorySheet: React.FC<Props> = ({ itemId, row, basedOnOptions, onClose, onSaved }) => {
  const { enqueueSnackbar } = useSnackbar();
  const formRef = useRef<FormikProps<HistoryFormValues>>(null);
  const initialValues = useMemo(() => historyFormValues(row), [row]);
  // an edited row of a kind legacy wrote itself ("Component Installed" …) keeps that type label
  const typeOptions = useMemo(
    () => Array.from(new Set([...HISTORY_TYPES, initialValues.history_type].filter(Boolean))),
    [initialValues.history_type]
  );

  const submit = (values: HistoryFormValues, helpers: FormikHelpers<HistoryFormValues>) => {
    const initialType = row ? initialValues.history_type : null;
    const errors = historyFormErrors(values, initialType);
    if (Object.keys(errors).length) {
      helpers.setErrors(errors);
      helpers.setSubmitting(false);
      return;
    }
    const body = historyPayload(values, initialType);
    const request = row
      ? axios.patch(`/v6/items/${itemId}/component-history/${row.id}`, body, { cache: false })
      : axios.post(`/v6/items/${itemId}/component-history`, body, { cache: false });
    request
      .then(() => {
        helpers.setSubmitting(false);
        enqueueSnackbar(row ? "History row saved" : "History row added", { variant: "success" });
        onSaved();
      })
      .catch((error) => {
        // a failed write keeps the sheet open with what was typed (BP-89)
        helpers.setSubmitting(false);
        handleAPIErrors(error, helpers.setErrors);
        const detail = error?.response?.data?.detail;
        enqueueSnackbar(typeof detail === "string" ? detail : "Error saving history row", { variant: "error" });
      });
  };

  return (
    <Sheet open onClose={onClose}>
      <SheetHeader onClose={onClose} title={row ? "Edit History Row" : "Add History Row"} />
      <SheetContent>
        <Formik initialValues={initialValues} onSubmit={submit} innerRef={formRef} enableReinitialize>
          {({ values, setFieldValue, errors }) => (
            <Form className="mui-form" data-testid="component-history-form">
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Field component={TextField} select fullWidth name="history_type" label="Type" required>
                    {typeOptions.map((t) => (
                      <MenuItem key={t} value={t}>
                        {t}
                      </MenuItem>
                    ))}
                    {initialValues.history_type ? null : <MenuItem value="">—</MenuItem>}
                  </Field>
                </Grid>
                <Grid item xs={6}>
                  <TenantDatePicker
                    label="Date"
                    required
                    fullWidth
                    value={values.record_date}
                    onChange={(iso) => setFieldValue("record_date", iso)}
                    error={Boolean(errors.record_date)}
                    helperText={errors.record_date}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Autocomplete
                    freeSolo
                    options={basedOnOptions}
                    value={values.based_on}
                    onChange={(_e, v) => setFieldValue("based_on", typeof v === "string" ? v : "")}
                    onInputChange={(_e, v, reason) => {
                      if (reason !== "reset") setFieldValue("based_on", v ?? "");
                    }}
                    renderInput={(params) => <MuiTextField {...params} label="Based On" />}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Field component={TextField} fullWidth name="ac_info" label="Aircraft REG/ENG No." />
                </Grid>
                <Grid item xs={6}>
                  <Field component={TextField} fullWidth name="ac_unit" label="A/C@Unit" type="number" />
                </Grid>
                <Grid item xs={6}>
                  <Field
                    component={TextField}
                    fullWidth
                    name="tsn"
                    label="TSN"
                    type="number"
                    disabled={values.tsn_unknown}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={values.tsn_unknown}
                        onChange={(e) => setFieldValue("tsn_unknown", e.target.checked)}
                      />
                    }
                    label="UNK"
                  />
                </Grid>
                <Grid item xs={6}>
                  <Field
                    component={TextField}
                    fullWidth
                    name="tso"
                    label="TSO"
                    type="number"
                    error={Boolean(errors.tso)}
                    helperText={errors.tso}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Field component={TextField} fullWidth multiline minRows={2} name="comment" label="Comment" />
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
      </SheetContent>
      <SheetActions>
        <CancelButton onClick={onClose} />
        <ActionButton onClick={() => formRef.current?.submitForm()}>{row ? "Save" : "Add"}</ActionButton>
      </SheetActions>
    </Sheet>
  );
};

export default ComponentHistorySheet;
